/**
 * Schedule label colors
 * Turns an admin-picked label color into the light, opaque tints the schedule layouts use: a pill
 * background with readable same-hue text, and a softer surface + border for cards and calendar
 * blocks. Tints are flattened over white (not translucent), so they read the same on any section.
 */

import { getReadableTextColor } from "@/features/landing-pages/_logic/color-utils";
import { blendHex } from "@/lib/color/hex";
import {
  SCHEDULE_DEFAULT_LABEL_COLOR,
  SCHEDULE_LABEL_BORDER_ALPHA,
  SCHEDULE_LABEL_SURFACE_ALPHA,
  SCHEDULE_LABEL_TINT_ALPHA,
} from "../constants";

const WHITE_HEX = "#ffffff";

/** The tints derived from one label color. */
export interface ScheduleLabelTint {
  /** Pill background: the label color lightly over white. */
  background: string;
  /** Pill text: the label color darkened until it passes WCAG AA on `background`. */
  text: string;
  /** Card or calendar block surface: a fainter tint than the pill. */
  surface: string;
  /** Card or calendar block border. */
  border: string;
}

/**
 * Derives the tinted pill, surface and border colors for a schedule label.
 *
 * @param color - The label's hex color, or null/empty for a label with no color set.
 * @returns The tints, built from `SCHEDULE_DEFAULT_LABEL_COLOR` when `color` is missing or invalid.
 * @throws Never.
 */
export function getScheduleLabelTint(color: string | null | undefined): ScheduleLabelTint {
  const base = color || SCHEDULE_DEFAULT_LABEL_COLOR;
  const background = blendHex(base, WHITE_HEX, SCHEDULE_LABEL_TINT_ALPHA);
  return {
    background,
    text: getReadableTextColor(base, background),
    surface: blendHex(base, WHITE_HEX, SCHEDULE_LABEL_SURFACE_ALPHA),
    border: blendHex(base, WHITE_HEX, SCHEDULE_LABEL_BORDER_ALPHA),
  };
}
